// Docs.tsx
import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";

// Adjust these paths to where you actually put the files
import CarbonBridgePolicyArchitecture from "./CarbonBridgePolicyArchitecture.tsx";
import CarbonCreditRulesDoc from "./CarbonCreditRulesDoc.tsx";
import BrokerAssistantPromptDoc from "./BrokerAssistantPromptDoc.tsx";
import CarbonBridgeTestPlanDoc from "./CarbonBridgeTestPlanDoc.tsx";
import CarbonBridgeTestCasesDoc from "./CarbonBridgeTestCasesDoc.tsx";

interface TabPanelProps {
  index: number;
  value: number;
  children?: React.ReactNode;
}

const TabPanel: React.FC<TabPanelProps> = ({ index, value, children }) => {
  if (value !== index) return null;
  return (
    <Box sx={{ pt: 3 }}>
      {children}
    </Box>
  );
};

const Docs: React.FC = () => {
  const [tab, setTab] = useState(0);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Hero / Header */}
      <Box
        sx={{
          pt: 8,
          pb: 4,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 60%, #0f172a 100%)`,
          color: "common.white",
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={isSmall ? "column" : "row"}
            spacing={3}
            alignItems={isSmall ? "flex-start" : "center"}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h3" fontWeight={700} gutterBottom>
                CarbonBridge / CarbonChain Docs
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.92, maxWidth: 720 }}>
                Internal design documentation for carbon-credit engine and
                MQTT energy monitoring assistant. Rules, prompts, and flows that
                keep credits compliant, auditable, and machine-verifiable.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label="R&D / Internal"
                color="secondary"
                variant="filled"
              />
              <Chip
                label="AI-Governed Rules"
                color="default"
                variant="outlined"
                sx={{ borderColor: "rgba(255,255,255,0.7)", color: "common.white" }}
              />
              <Chip
                label="v1 Draft"
                color="default"
                variant="outlined"
                sx={{ borderColor: "rgba(255,255,255,0.5)", color: "common.white" }}
              />
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Main content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          {/* Tabs header */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              px: 2,
              pt: 1.5,
              bgcolor: "background.paper",
            }}
          >
            <Stack
              direction={isSmall ? "column" : "row"}
              spacing={1.5}
              alignItems={isSmall ? "flex-start" : "center"}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Documentation Sections
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Switch between the technical documents.
                </Typography>
              </Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <Tabs
                  value={tab}
                  onChange={handleChange}
                  variant={isSmall ? "scrollable" : "standard"}
                  scrollButtons={isSmall ? "auto" : undefined}
                  textColor="primary"
                  indicatorColor="primary"
                >
                  <Tab
                    label="High-level Architecture"
                    sx={{ textTransform: "none", fontWeight: 500 }}
                  />
                  <Tab
                    label="Carbon Credit Rules"
                    sx={{ textTransform: "none", fontWeight: 500 }}
                  />
                  <Tab
                    label="MQTT Broker Prompts"
                    sx={{ textTransform: "none", fontWeight: 500 }}
                  />
                  <Tab
                    label="Test Strategy & Plan"
                    sx={{ textTransform: "none", fontWeight: 500 }}
                  />
                  <Tab
                    label="Test Cases"
                    sx={{ textTransform: "none", fontWeight: 500 }}
                  />
                </Tabs>
                <Box>
                  <button
                    onClick={handlePrint}
                    style={{
                      marginLeft: 16,
                      padding: '6px 16px',
                      fontSize: 15,
                      borderRadius: 4,
                      border: '1px solid #818cf8',
                      background: '#fff',
                      color: '#6366f1',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    Print
                  </button>
                </Box>
              </Stack>
            </Stack>
          </Box>

          {/* Content area */}
          <Box sx={{ p: { xs: 2.5, md: 3.5 }, bgcolor: "background.default" }}>
            <TabPanel value={tab} index={0}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  High-level Architecture
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Overview of the CarbonBridge system architecture and design principles.
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <CarbonBridgePolicyArchitecture />
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Carbon Credit Rulebook
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  End-to-end rules for creation, validation, pricing, retirement,
                  and blockchain alignment of tokenised carbon credits.
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <CarbonCreditRulesDoc />
            </TabPanel>

            <TabPanel value={tab} index={2}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  MQTT Energy Broker – AI Prompt Design
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  System messages, allowed topics, JSON-only responses and SQL
                  extraction prompts for MQTT energy monitoring assistant.
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <BrokerAssistantPromptDoc />
            </TabPanel>

            <TabPanel value={tab} index={3}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Test Strategy & Plan
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Comprehensive test strategy for the LLM-governed carbon credit engine,
                  aligned with international testing, quality, security, and AI-risk
                  standards.
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <CarbonBridgeTestPlanDoc />
            </TabPanel>

            <TabPanel value={tab} index={4}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Test Cases
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Detailed test case templates for functional, non-functional, and compliance/AI scenarios.
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <CarbonBridgeTestCasesDoc />
            </TabPanel>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Docs;
